import { inject, Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { forkJoin, map, Observable, of } from 'rxjs';
import { Bundle, Medication, MedicationRequest, MedicationDispense } from 'fhir/r4';
import { backendEndPointToken } from '../app.config';
import { UtilityService } from '../shared/utility.service';

export interface PharmacyMetrics {
    newPrescriptions: number;
    pendingDispenses: number;
    lowStockItems: number;
    expiredItems: number;
    totalItems: number;
}

@Injectable({
    providedIn: 'root'
})
export class PharmacyDashboardService {
    private http = inject(HttpClient);
    private backendEndPoint = inject(backendEndPointToken);
    private utilityService = inject(UtilityService);

    private readonly DEFAULT_LOW_STOCK_THRESHOLD = 10;

    getMetrics(): Observable<PharmacyMetrics> {
        const prescriptions$ = this.http.get<Bundle<MedicationRequest>>(
            `${this.backendEndPoint}/MedicationRequest?status=active&intent=order&_summary=count`
        ).pipe(map(b => b.total ?? 0));

        const dispenses$ = this.http.get<Bundle<MedicationDispense>>(
            `${this.backendEndPoint}/MedicationDispense?status=in-progress,on-hold&_summary=count`
        ).pipe(map(b => b.total ?? 0));

        const medications$ = this.getMedications().pipe(
            map(bundle => {
                const entries = bundle.entry || [];
                const medications = entries.map(e => e.resource).filter((r): r is Medication => !!r);

                let lowStockCount = 0;
                let expiredCount = 0;
                const today = new Date();

                medications.forEach(med => {
                    // Check Low Stock
                    const stock = this.utilityService.getMedicationTotalRemaining(med);
                    const config = this.utilityService.getMedicationLowStockConfig(med);
                    const threshold = config?.threshold ?? this.DEFAULT_LOW_STOCK_THRESHOLD;

                    if (stock && typeof stock.value === 'number') {
                        if (this.utilityService.isBelowLowStockThreshold(stock.value, threshold)) {
                            lowStockCount++;
                        }
                    }

                    // Check Expired
                    if (med.batch?.expirationDate) {
                        const expiry = new Date(med.batch.expirationDate);
                        if (expiry < today) {
                            expiredCount++;
                        }
                    }
                });

                return {
                    lowStockItems: lowStockCount,
                    expiredItems: expiredCount,
                    totalItems: medications.length
                };
            })
        );

        return forkJoin({
            newPrescriptions: prescriptions$,
            pendingDispenses: dispenses$,
            stockMetrics: medications$
        }).pipe(
            map(results => ({
                newPrescriptions: results.newPrescriptions,
                pendingDispenses: results.pendingDispenses,
                lowStockItems: results.stockMetrics.lowStockItems,
                expiredItems: results.stockMetrics.expiredItems,
                totalItems: results.stockMetrics.totalItems
            }))
        );
    }

    getMedications(): Observable<Bundle<Medication>> {
        return this.http.get<Bundle<Medication>>(`${this.backendEndPoint}/Medication?_count=1000`);
    }

    getExpiredMedications(): Observable<Bundle<Medication>> {
        const today = new Date();
        return this.getMedications().pipe(
            map(bundle => {
                const entries = (bundle.entry || []).filter(e => {
                    const med = e.resource as Medication;
                    return med?.batch?.expirationDate && new Date(med.batch.expirationDate) < today;
                });
                return { ...bundle, entry: entries, total: entries.length };
            })
        );
    }

    getLowStockMedications(): Observable<Bundle<Medication>> {
        return this.getMedications().pipe(
            map(bundle => {
                const entries = (bundle.entry || []).filter(e => {
                    const med = e.resource as Medication;
                    const stock = this.utilityService.getMedicationTotalRemaining(med);
                    const config = this.utilityService.getMedicationLowStockConfig(med);
                    const threshold = config?.threshold ?? this.DEFAULT_LOW_STOCK_THRESHOLD;
                    return stock && typeof stock.value === 'number' &&
                        this.utilityService.isBelowLowStockThreshold(stock.value, threshold);
                });
                return { ...bundle, entry: entries, total: entries.length };
            })
        );
    }
}
